import { useState } from 'react';
import { demeterApi, demeterEmailSubscriptionsApi } from '../../../../../Apis/Apis';
import { DemeterDataFrequency, DemeterEmailSubscriptionType } from '../../../../../Generated/Raven-Demeter';
import useApiWithoutAutoExecute from '../../../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../../../Components/Form/Buttons/LinkButton';
import RadioInput from '../../../../Components/Form/Inputs/RadioInput';
import TextArea from '../../../../Components/Form/Inputs/TextArea';
import TextInput from '../../../../Components/Form/Inputs/TextInput';
import ActionModal from '../../../../Components/Modals/ActionModal/ActionModal';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { ContactAgentRequest, LiquidityRiskCalculationInputs } from '../../RiskDefinitions';
import { DairyRevenueProtectionCalculationInputs, GeneralRiskCalculationInputs } from '../DairyRevenueProtection/DairyRevenueProtectionDefinitions';
import styles from '../InsuranceQuoteEstimator.module.scss';

interface ICoverageLevelsActionsProps {
    calculationInputs: GeneralRiskCalculationInputs;
    subscriptionType: DemeterEmailSubscriptionType;
    useDairyAgent?: boolean;
}

const CoverageLevelsActions: React.FC<ICoverageLevelsActionsProps> = (props: ICoverageLevelsActionsProps) => {
    // Application hooks.
    const [translations] = useLanguage();
    const [showContactAgentModal, setShowContactAgentModal] = useState(false);
    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    const [subscriptionFrequency, setSubscriptionFrequency] = useState<DemeterDataFrequency>(DemeterDataFrequency.Daily);
    const [subscriptionName, setSubscriptionName] = useState<string>('');
    const [contactAgentRequest, setContactAgentRequest] = useState<ContactAgentRequest>({
        subject: translations.risk.margin.fields.livestockRevenueProtectionQuote,
        message: '',
    });

    const [, activateDemeterSubscription] = useApiWithoutAutoExecute(
        () => {
            if (props.subscriptionType === DemeterEmailSubscriptionType.DairyInsurancePriceSubscription) {
                return demeterEmailSubscriptionsApi.activateDemeterEmailSubscription({
                    name: subscriptionName,
                    frequency: subscriptionFrequency,
                    emailSubscriptionType: props.subscriptionType,
                    preferences: {
                        insuranceYear: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).insuranceYear.toString(),
                        coverageEffectiveDate: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).coverageEffectiveDate,
                        insuredSharedPercentage: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).insuredSharedPercentage.toString(),
                        insuranceState: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).insuranceState,
                        componentBasedPercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).componentBasedPercent?.toString() ?? '',
                        classBasePercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).classBasePercent.toString(),
                        coverageLevelPercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).coverageLevelPercent.toString(),
                        protectionFactor: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).protectionFactor.toString(),
                        declaredSharePercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).declaredSharePercent.toString(),
                        declaredMilkProduction: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).declaredMilkProduction.toString(),
                        class3MilkPercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).class3MilkPercent.toString(),
                        class4MilkPercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).class4MilkPercent.toString(),
                        fatPercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).fatPercent?.toString() ?? '',
                        proteinPercent: (props.calculationInputs as DairyRevenueProtectionCalculationInputs).proteinPercent?.toString() ?? '',
                        componentWeightingFactorPercent:
                            (props.calculationInputs as DairyRevenueProtectionCalculationInputs).componentWeightingFactorPercent?.toString() ?? '',
                    },
                });
            }
            return demeterEmailSubscriptionsApi.activateDemeterEmailSubscription({
                name: subscriptionName,
                frequency: subscriptionFrequency,
                emailSubscriptionType: props.subscriptionType,
                preferences: {
                    insuranceYear: (props.calculationInputs as LiquidityRiskCalculationInputs).insuranceYear.toString(),
                    coverageEffectiveDate: (props.calculationInputs as LiquidityRiskCalculationInputs).coverageEffectiveDate,
                    insuredSharedPercentage: (props.calculationInputs as LiquidityRiskCalculationInputs).insuredSharedPercentage.toString(),
                    endorsementLength: (props.calculationInputs as LiquidityRiskCalculationInputs).endorsementLength.join(','),
                    endWeight: (props.calculationInputs as LiquidityRiskCalculationInputs).endWeight.toString(),
                    insuranceState: (props.calculationInputs as LiquidityRiskCalculationInputs).insuranceState,
                    commodity: (props.calculationInputs as LiquidityRiskCalculationInputs).commodity,
                    type: (props.calculationInputs as LiquidityRiskCalculationInputs).type.join(','),
                    animalHeadCount: (props.calculationInputs as LiquidityRiskCalculationInputs).animalHeadCount.toString(),
                    costPerPoundOfGrain: (props.calculationInputs as LiquidityRiskCalculationInputs).costPerPoundOfGrain?.toString() ?? '',
                    shortWeight: (props.calculationInputs as LiquidityRiskCalculationInputs).shortWeight?.toString() ?? '',
                    purchaseCostPerAnimal: (props.calculationInputs as LiquidityRiskCalculationInputs).purchaseCostPerAnimal?.toString() ?? '',
                },
            });
        },
        {
            successMessage: translations.risk.insuranceQuoteEstimator.messages.subscriptionSuccess,
            errorMessage: translations.risk.insuranceQuoteEstimator.messages.subscriptionFailure,
        },
    );

    const [, refreshContactLivestockAgent] = useApiWithoutAutoExecute(
        () =>
            demeterApi.contactLivestockAgent({
                subject: contactAgentRequest.subject,
                message: contactAgentRequest.message,
                reinsuranceYear: props.calculationInputs.insuranceYear,
                asOfDate: props.calculationInputs.coverageEffectiveDate,
                state: props.calculationInputs.insuranceState,
                commodity: (props.calculationInputs as LiquidityRiskCalculationInputs).commodity,
                subCommodities: (props.calculationInputs as LiquidityRiskCalculationInputs).type,
                endorsementLengths: (props.calculationInputs as LiquidityRiskCalculationInputs).endorsementLength,
            }),
        { successMessage: translations.risk.margin.messages.contactBrokerSuccess, errorMessage: translations.risk.margin.messages.contactBrokerFailure },
    );

    // TODO: Will need to replace this with a contact dairy agent.
    const [, refreshContactDairyAgent] = useApiWithoutAutoExecute(
        () =>
            demeterApi.contactLivestockAgent({
                subject: contactAgentRequest.subject,
                message: contactAgentRequest.message,
                reinsuranceYear: props.calculationInputs.insuranceYear,
                asOfDate: props.calculationInputs.coverageEffectiveDate,
                state: props.calculationInputs.insuranceState,
                commodity: (props.calculationInputs as LiquidityRiskCalculationInputs).commodity,
                subCommodities: (props.calculationInputs as LiquidityRiskCalculationInputs).type,
                endorsementLengths: (props.calculationInputs as LiquidityRiskCalculationInputs).endorsementLength,
            }),
        { successMessage: translations.risk.margin.messages.contactBrokerSuccess, errorMessage: translations.risk.margin.messages.contactBrokerFailure },
    );

    return (
        <div className={styles.coverage_levels_actions}>
            <LinkButton
                title={translations.risk.insuranceQuoteEstimator.actions.subscribe}
                type={LinkButtonType.White}
                onClick={() => setShowSubscribeModal(true)}
                track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'SubscribeClicked' }}
            />
            <LinkButton
                title={translations.risk.insuranceQuoteEstimator.actions.contactAgent}
                type={LinkButtonType.Blue}
                onClick={() => setShowContactAgentModal(true)}
                track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'ContactAgentClicked' }}
            />
            <ActionModal
                header={translations.risk.insuranceQuoteEstimator.actions.contactAgent}
                showModal={showContactAgentModal}
                actionButtonName={translations.actions.send}
                handleAction={() => {
                    if (props.useDairyAgent) {
                        refreshContactDairyAgent();
                        return;
                    }
                    refreshContactLivestockAgent();
                    setShowContactAgentModal(false);
                }}
                handleCancel={() => setShowContactAgentModal(false)}
                actionButtonDisabled={false}
                track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'ModalContactAgent' }}
            >
                <div className={styles.coverage_levels_actions_modal_content}>
                    <div>
                        <p>{translations.risk.margin.text.toContactAgent}</p>
                    </div>

                    <div className={styles.coverage_levels_actions_contact_modal_field}>
                        <p className={styles.coverage_levels_actions_contact_modal_field_title}>{translations.risk.margin.fields.subject}</p>
                        <p className={styles.coverage_levels_actions_contact_modal_field}>{translations.risk.margin.fields.livestockRevenueProtectionQuote}</p>
                    </div>
                    <div className={styles.coverage_levels_actions_contact_modal_field}>
                        <TextArea
                            title={translations.words.message}
                            value={contactAgentRequest?.message}
                            handleTextChange={(value) => setContactAgentRequest({ ...contactAgentRequest, message: value })}
                        />
                    </div>
                </div>
            </ActionModal>
            <ActionModal
                header={translations.risk.insuranceQuoteEstimator.headers.subscribe}
                showModal={showSubscribeModal}
                actionButtonName={translations.actions.subscribe}
                handleAction={() => {
                    activateDemeterSubscription({ frequency: subscriptionFrequency });
                    setShowSubscribeModal(false);
                }}
                handleCancel={() => setShowSubscribeModal(false)}
                actionButtonDisabled={!subscriptionName}
                track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'ModalEmailSubscription' }}
            >
                <div className={styles.coverage_levels_actions_modal_content}>
                    <div>{translations.risk.insuranceQuoteEstimator.text.subscription}</div>
                    <div className={styles.coverage_levels_actions_contact_modal_field}>
                        <TextInput
                            title={translations.risk.insuranceQuoteEstimator.text.nameSubscription}
                            value={subscriptionName}
                            handleTextChange={setSubscriptionName}
                            track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'NameEmailSubscriptionChanged' }}
                            required
                        />
                    </div>
                    <div className={styles.coverage_levels_actions_contact_modal_field}>
                        <RadioInput
                            title={translations.risk.insuranceQuoteEstimator.text.dailySubscription}
                            isChecked={subscriptionFrequency === DemeterDataFrequency.Daily}
                            handleIsChecked={(value) => value && setSubscriptionFrequency(DemeterDataFrequency.Daily)}
                            track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'DailyEmailSubscriptionSelected' }}
                        />
                    </div>
                    <div className={styles.coverage_levels_actions_contact_modal_field}>
                        <RadioInput
                            title={translations.risk.insuranceQuoteEstimator.text.weeklySubscription}
                            isChecked={subscriptionFrequency === DemeterDataFrequency.Weekly}
                            handleIsChecked={(value) => value && setSubscriptionFrequency(DemeterDataFrequency.Weekly)}
                            track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'WeeklyEmailSubscriptionSelected' }}
                        />
                    </div>
                </div>
            </ActionModal>
        </div>
    );
};

export default CoverageLevelsActions;
